import { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send, Loader2, Sparkles, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AIChatbot = ({ onToolSuggestion }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Get or create session ID from localStorage
    let storedSessionId = localStorage.getItem("chatbot_session_id");
    if (!storedSessionId) {
      storedSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem("chatbot_session_id", storedSessionId);
    }
    setSessionId(storedSessionId);

    // Fetch suggested questions
    fetchSuggestedQuestions();

    // Load chat history
    if (storedSessionId) {
      loadChatHistory(storedSessionId);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchSuggestedQuestions = async () => {
    try {
      const res = await axios.get(`${API}/chatbot/suggested-questions`);
      setSuggestedQuestions(res.data.questions || []);
    } catch (error) {
      console.error("Failed to fetch suggested questions:", error);
    }
  };

  const loadChatHistory = async (sid) => {
    try {
      const res = await axios.get(`${API}/chatbot/history/${sid}`);
      if (res.data.messages && res.data.messages.length > 0) {
        setMessages(res.data.messages.map(m => ({
          role: m.role,
          content: m.content
        })));
      }
    } catch (error) {
      console.error("Failed to load chat history:", error);
    }
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;

    const userMessage = { role: "user", content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const res = await axios.post(`${API}/chatbot/chat`, {
        message: messageText,
        session_id: sessionId
      });

      const assistantMessage = { role: "assistant", content: res.data.response };
      setMessages(prev => [...prev, assistantMessage]);

      // Handle tool suggestions
      if (res.data.suggested_tool && onToolSuggestion) {
        onToolSuggestion(res.data.suggested_tool);
      }
    } catch (error) {
      console.error("Chat error:", error);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "I apologize, but I'm having trouble processing your request. Please try again or contact our team directly." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleQuestionClick = (question) => {
    sendMessage(question);
  };

  const clearChat = async () => {
    if (sessionId) {
      try {
        await axios.delete(`${API}/chatbot/session/${sessionId}`);
      } catch (error) {
        console.error("Failed to clear session:", error);
      }
    }
    setMessages([]);
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("chatbot_session_id", newSessionId);
    setSessionId(newSessionId);
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-20 right-6 z-[100] bg-brand hover:bg-brand-dark text-white rounded-full p-4 shadow-lg transition-all duration-300 hover:scale-105 flex items-center gap-2"
          data-testid="chatbot-toggle"
        >
          <MessageCircle className="h-6 w-6" />
          <span className="hidden sm:inline font-medium">Ask Advisor</span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-20 right-6 z-[100] w-[380px] max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          style={{ height: "min(550px, calc(100vh - 150px))" }}
          data-testid="chatbot-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-brand to-brand-dark text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-full p-2">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Compliance Advisor</h3>
                <p className="text-xs text-white/80">Powered by AI</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={clearChat}
                className="text-white/70 hover:text-white text-xs underline"
              >
                Clear
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="text-white/70 hover:text-white p-1"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
            {messages.length === 0 ? (
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-sm text-slate-600 mb-3">
                    Hello! I&apos;m your Compliance & Training Advisor. I can help you with:
                  </p>
                  <ul className="text-sm text-slate-500 space-y-1">
                    <li>• Certification recommendations</li>
                    <li>• Compliance requirements</li>
                    <li>• Training guidance</li>
                    <li>• ISO standards information</li>
                  </ul>
                </div>
                
                {suggestedQuestions.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-400 font-medium">Try asking:</p>
                    {suggestedQuestions.slice(0, 4).map((q, i) => (
                      <button
                        key={i}
                        onClick={() => handleQuestionClick(q)}
                        className="w-full text-left bg-white hover:bg-brand/5 border border-slate-200 hover:border-brand/30 rounded-lg p-3 text-sm text-slate-700 transition-colors flex items-center justify-between group"
                      >
                        <span className="line-clamp-2">{q}</span>
                        <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-brand flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2 ${
                      msg.role === "user"
                        ? "bg-brand text-white rounded-br-md"
                        : "bg-white text-slate-700 shadow-sm border border-slate-100 rounded-bl-md"
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))
            )}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm border border-slate-100">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-brand" />
                    <span className="text-sm text-slate-500">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <form onSubmit={handleSubmit} className="p-4 border-t border-slate-200 bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about certifications, training..."
                className="flex-1 border border-slate-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                disabled={isLoading}
                data-testid="chatbot-input"
              />
              <Button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="bg-brand hover:bg-brand-dark text-white px-4"
                data-testid="chatbot-send"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-2 text-center">
              AI assistant • Not legal advice • <a href="/contact" className="text-brand hover:underline">Contact team</a>
            </p>
          </form>
        </div>
      )}
    </>
  );
};

export default AIChatbot;
